import { useId } from "react";

import { ChartPatterns } from "@/components/Charts/ChartPatterns";
import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import type { CommunityFeedSentimentMonthlyPoint } from "@/queries/analytics/community-feed-reporting-types";
import { useTranslation } from "@/translations";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "@/ui-adapters/Recharts";

export const sentimentColors = {
  positive: dataColors.green,
  neutral: "var(--color-yellow-500)",
  negative: "var(--color-background-danger)",
} as const;

interface Props {
  data: CommunityFeedSentimentMonthlyPoint[];
  labels: {
    analyzedPosts: string;
    insufficientData: string;
    negative: string;
    neutral: string;
    positive: string;
    currentPeriod: string;
    yAxis: string;
  };
}

export function SentimentDistributionChart({ data, labels }: Props): React.ReactNode {
  const { i18n } = useTranslation();
  const positivePatternId = useId();
  const neutralPatternId = useId();
  const negativePatternId = useId();
  const chartData = data.map((point) => ({
    ...point,
    negativePercentage: point.distribution.negativePercentage,
    neutralPercentage: point.distribution.neutralPercentage,
    positivePercentage: point.distribution.positivePercentage,
  }));

  return (
    <ResponsiveContainer minHeight={240} className="flex-1">
      <BarChart
        className="no-top-bottom-lines"
        data={chartData}
        margin={{
          top: 16,
          right: 8,
          left: 0,
        }}
      >
        <ChartPatterns.Stripes id={positivePatternId} color={sentimentColors.positive} />
        <ChartPatterns.Stripes id={neutralPatternId} color={sentimentColors.neutral} />
        <ChartPatterns.Stripes id={negativePatternId} color={sentimentColors.negative} />

        <CartesianGrid stroke={dataColors.grid} vertical={false} />
        <XAxis
          dataKey="x"
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: string) => formatDate(i18n, "monthYearShort", value)}
          fontSize={12}
        />
        <YAxis
          width={80}
          domain={[0, 100]}
          axisLine={false}
          tickLine={false}
          tickCount={6}
          tickFormatter={(value: number) => `${value}%`}
          label={
            <Label
              value={labels.yAxis}
              position="left"
              angle={-90}
              offset={-15}
              textAnchor="middle"
              fontSize={14}
              fill={dataColors.label}
            />
          }
          fontSize={12}
        />
        <Tooltip content={<SentimentTooltip labels={labels} />} />

        <SentimentBar dataKey="negativePercentage" color={sentimentColors.negative} patternId={negativePatternId} />
        <SentimentBar dataKey="neutralPercentage" color={sentimentColors.neutral} patternId={neutralPatternId} />
        <SentimentBar dataKey="positivePercentage" color={sentimentColors.positive} patternId={positivePatternId} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function SentimentBar({
  dataKey,
  color,
  patternId,
}: {
  dataKey: string;
  color: string;
  patternId: string;
}): React.ReactNode {
  return (
    <Bar
      dataKey={dataKey}
      stackId="sentiment"
      barSize={36}
      shape={(props) => {
        const { payload, ...rectangleProps } = props as React.ComponentProps<typeof Rectangle> & {
          payload?: CommunityFeedSentimentMonthlyPoint;
        };

        return <Rectangle {...rectangleProps} fill={payload?.isCurrentMonth ? `url(#${patternId})` : color} />;
      }}
    />
  );
}

function SentimentTooltip({
  active,
  payload,
  labels,
}: Partial<{
  active: boolean;
  payload: { payload: CommunityFeedSentimentMonthlyPoint }[];
}> & {
  labels: Props["labels"];
}): React.ReactNode {
  const { i18n } = useTranslation();
  const point = payload?.[0]?.payload;

  if (!active || !point) {
    return null;
  }

  return (
    <ChartTooltip>
      <span className="block">{formatDate(i18n, "monthYear", point.x)}</span>
      <span className="block">
        {labels.analyzedPosts}: {point.analyzedPosts}
      </span>
      {point.hasSufficientData ? (
        <>
          <span className="block">
            {labels.positive}: {point.distribution.positivePercentage}%
          </span>
          <span className="block">
            {labels.neutral}: {point.distribution.neutralPercentage}%
          </span>
          <span className="block">
            {labels.negative}: {point.distribution.negativePercentage}%
          </span>
        </>
      ) : (
        <span className="block">{labels.insufficientData}</span>
      )}
      {point.isCurrentMonth && <span className="block">{labels.currentPeriod}</span>}
    </ChartTooltip>
  );
}
